import React, { useEffect, useMemo, useState, useRef } from "react";
import { Calendar, Plus, X, ChevronLeft, ChevronRight, Loader2, MapPin, Users as UsersIcon, Trash2, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth.jsx";
import Layout, { PageHeader } from "@/components/Layout";
import { getNav } from "@/pages/navConfigs";

const CATEGORY_COLORS = {
  personal: { bg: "bg-orange-100", text: "text-orange-700", dot: "bg-orange-500" },
  work: { bg: "bg-sky-100", text: "text-sky-700", dot: "bg-sky-500" },
  meeting: { bg: "bg-sky-100", text: "text-sky-700", dot: "bg-sky-500" },
  event: { bg: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" },
  travel: { bg: "bg-emerald-100", text: "text-emerald-700", dot: "bg-emerald-500" },
  reminder: { bg: "bg-fuchsia-100", text: "text-fuchsia-700", dot: "bg-fuchsia-500" },
};

const localDateStr = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function SchedulerPage() {
  const { user, customRole } = useAuth();
  const [view, setView] = useState("month"); // day | week | month | year
  const [anchor, setAnchor] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(null); // { date }
  const [selectedDate, setSelectedDate] = useState(localDateStr(new Date()));
  const [viewingEntry, setViewingEntry] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/scheduler/entries");
      setEntries(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const deleteEntry = async (e) => {
    if (!window.confirm(`Delete "${e.title}"?`)) return;
    try {
      await api.delete(`/scheduler/entries/${e.entry_id}`);
      toast.success("Deleted");
      setViewingEntry(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
  };

  const entriesByDate = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      const start = e.start_date;
      const end = e.end_date || e.start_date;
      if (!start) continue;
      // Multi-day: expand into every date it covers
      const [sy, sm, sd] = start.split("-").map(Number);
      const [ey, em, ed] = end.split("-").map(Number);
      const startD = new Date(sy, sm - 1, sd);
      const endD = new Date(ey, em - 1, ed);
      for (let d = new Date(startD); d <= endD; d.setDate(d.getDate() + 1)) {
        const key = localDateStr(d);
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(e);
      }
    }
    return map;
  }, [entries]);

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <PageHeader
          title="Scheduler"
          subtitle="Your personal calendar. Auto-populates with your events and meetings — add notes, tasks and physical meetings."
          icon={Calendar}
          accent="orange"
        />

        <div className="flex flex-wrap items-center gap-2 mb-4">
          <ViewToggle view={view} onChange={setView} />
          <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1">
            <button onClick={() => setAnchor(prevAnchor(anchor, view, -1))} className="p-1.5 hover:bg-slate-50 rounded-full" data-testid="sch-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => { setAnchor(new Date()); setSelectedDate(localDateStr(new Date())); }} className="text-xs font-bold px-3 py-1 hover:bg-slate-50 rounded-full">Today</button>
            <button onClick={() => setAnchor(prevAnchor(anchor, view, +1))} className="p-1.5 hover:bg-slate-50 rounded-full" data-testid="sch-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="font-display font-bold text-xl ml-2">{titleFor(anchor, view)}</div>
          <div className="flex-1" />
          <button onClick={() => setShowAdd({ date: selectedDate })} className="btn-primary" data-testid="sch-add-btn">
            <Plus className="w-4 h-4" /> New entry
          </button>
        </div>

        {loading ? <div className="dashboard-card p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          : view === "month" ? <MonthView anchor={anchor} entriesByDate={entriesByDate} onPickDay={(d) => { setSelectedDate(d); setShowAdd({ date: d }); }} onSelectDay={setSelectedDate} selectedDate={selectedDate} onEntryClick={setViewingEntry} />
          : view === "week" ? <WeekView anchor={anchor} entriesByDate={entriesByDate} onPickDay={(d) => { setSelectedDate(d); setShowAdd({ date: d }); }} onEntryClick={setViewingEntry} />
          : view === "day" ? <DayView anchor={anchor} entriesByDate={entriesByDate} onAdd={(d) => setShowAdd({ date: d })} onEntryClick={setViewingEntry} />
          : <YearView anchor={anchor} entriesByDate={entriesByDate} onPickDay={(d) => { setAnchor(new Date(d.split("-")[0], d.split("-")[1] - 1, d.split("-")[2])); setView("day"); setSelectedDate(d); }} />}

        {showAdd && <AddEntryModal date={showAdd.date} onClose={() => setShowAdd(null)} onSaved={() => { setShowAdd(null); load(); }} />}
        {viewingEntry && (
          <EntryDetailModal
            entry={viewingEntry}
            canDelete={!viewingEntry.readonly && (viewingEntry.owner_id === user.user_id || user.role === "admin")}
            onDelete={() => deleteEntry(viewingEntry)}
            onClose={() => setViewingEntry(null)}
          />
        )}
      </div>
    </Layout>
  );
}

function EntryDetailModal({ entry, canDelete, onDelete, onClose }) {
  const cat = CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.personal;
  const time = !entry.all_day && entry.start_at && entry.start_at.length >= 16 ? `${entry.start_at.slice(11, 16)} – ${(entry.end_at || "").slice(11, 16)}` : "All day";
  const [resending, setResending] = useState(false);
  const canResend = !entry.readonly && entry.event_id && entry.kind === "school_meeting" && canDelete;

  const resend = async () => {
    setResending(true);
    try {
      const r = await api.post(`/scheduler/entries/${entry.entry_id}/resend-notification`);
      const n = r.data?.sent_to ?? 0;
      if (n === 0) toast.warning("No principal is assigned to this event yet.");
      else toast.success(`Notification resent to ${n} principal${n === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally { setResending(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="sch-entry-modal">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${cat.bg} ${cat.text}`}>{entry.category || entry.kind}</span>
            <h3 className="font-display font-black text-xl mt-2">{entry.title}</h3>
            <div className="text-xs text-slate-500 mt-1">{entry.start_date}{entry.end_date && entry.end_date !== entry.start_date ? ` → ${entry.end_date}` : ""} · {time}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-2 text-sm">
          {entry.location && <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-slate-400" /> {entry.location}</div>}
          {entry.description && <div className="text-slate-600 whitespace-pre-wrap">{entry.description}</div>}
          {entry.readonly && <div className="text-[11px] text-slate-500 italic">This entry is auto-generated from {entry.kind === "event" ? "an event" : "a meeting"} — manage it from the source.</div>}
        </div>
        {(canDelete || canResend) && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-between flex-wrap gap-2">
            {canResend ? (
              <button onClick={resend} disabled={resending} className="text-sm text-orange-600 hover:underline inline-flex items-center gap-1 disabled:opacity-50" data-testid="sch-entry-resend">
                {resending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                {resending ? "Sending…" : "Resend principal notification"}
              </button>
            ) : <span />}
            {canDelete && (
              <button onClick={onDelete} className="text-sm text-rose-600 hover:underline inline-flex items-center gap-1" data-testid="sch-entry-delete">
                <Trash2 className="w-4 h-4" /> Delete entry
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================
// NAV HELPERS
// ============================================================
function prevAnchor(d, view, dir) {
  const nd = new Date(d);
  if (view === "day") nd.setDate(nd.getDate() + dir);
  else if (view === "week") nd.setDate(nd.getDate() + 7 * dir);
  else if (view === "month") nd.setMonth(nd.getMonth() + dir);
  else nd.setFullYear(nd.getFullYear() + dir);
  return nd;
}
function titleFor(d, view) {
  const opts = { month: "long", year: "numeric" };
  if (view === "year") return d.getFullYear();
  if (view === "day") return d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  if (view === "week") {
    const first = new Date(d); first.setDate(d.getDate() - d.getDay());
    const last = new Date(first); last.setDate(first.getDate() + 6);
    return `${first.toLocaleDateString(undefined, { day: "numeric", month: "short" })} – ${last.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
  }
  return d.toLocaleDateString(undefined, opts);
}
function ViewToggle({ view, onChange }) {
  return (
    <div className="inline-flex bg-white border border-slate-200 rounded-full p-1" data-testid="sch-view-toggle">
      {["day", "week", "month", "year"].map((v) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          className={`px-3 py-1 text-xs font-bold rounded-full capitalize ${view === v ? "brand-gradient text-white" : "text-slate-600 hover:bg-slate-50"}`}
          data-testid={`sch-view-${v}`}
        >{v}</button>
      ))}
    </div>
  );
}

// ============================================================
// MONTH VIEW
// ============================================================
function MonthView({ anchor, entriesByDate, onPickDay, onSelectDay, selectedDate, onEntryClick }) {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(anchor.getFullYear(), anchor.getMonth(), d);
    const iso = localDateStr(dt);
    cells.push({ iso, day: d, entries: entriesByDate.get(iso) || [] });
  }
  const todayIso = localDateStr(new Date());
  return (
    <div className="dashboard-card p-3">
      <div className="grid grid-cols-7 text-[10px] uppercase font-bold text-slate-500 mb-2 text-center">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => c == null ? <div key={`e${i}`} /> : (
          <button
            key={c.iso}
            onDoubleClick={() => onPickDay(c.iso)}
            onClick={() => onSelectDay(c.iso)}
            className={`text-left min-h-[92px] p-2 rounded-lg border transition-colors ${selectedDate === c.iso ? "border-orange-400 bg-orange-50/40" : todayIso === c.iso ? "border-slate-400" : "border-slate-100 hover:border-orange-200"}`}
            data-testid={`sch-day-${c.iso}`}
          >
            <div className="text-xs font-bold text-slate-700">{c.day}</div>
            <div className="mt-1 space-y-0.5">
              {c.entries.slice(0, 3).map((e, idx) => {
                const cat = CATEGORY_COLORS[e.category] || CATEGORY_COLORS.personal;
                return (
                  <div
                    key={idx}
                    onClick={(ev) => { ev.stopPropagation(); onEntryClick?.(e); }}
                    className={`text-[10px] px-1.5 py-0.5 rounded ${cat.bg} ${cat.text} truncate font-semibold cursor-pointer hover:opacity-80`}
                    data-testid={`sch-entry-chip-${e.entry_id}`}
                  >{e.title}</div>
                );
              })}
              {c.entries.length > 3 && <div className="text-[9px] text-slate-500">+{c.entries.length - 3} more</div>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// WEEK VIEW  (Apple-like column view)
// ============================================================
function WeekView({ anchor, entriesByDate, onPickDay, onEntryClick }) {
  const first = new Date(anchor); first.setDate(anchor.getDate() - anchor.getDay());
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(first); d.setDate(first.getDate() + i); return d; });
  const todayIso = localDateStr(new Date());
  return (
    <div className="dashboard-card p-2 overflow-x-auto">
      <div className="grid grid-cols-7 min-w-[900px] gap-1">
        {days.map((d) => {
          const iso = localDateStr(d);
          const list = entriesByDate.get(iso) || [];
          return (
            <div key={iso} className={`min-h-[420px] p-2 rounded-lg border ${todayIso === iso ? "border-orange-400 bg-orange-50/30" : "border-slate-100"}`}>
              <div className="flex items-center justify-between mb-1">
                <div>
                  <div className="text-[10px] uppercase font-bold text-slate-500">{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
                  <div className="text-lg font-black text-slate-800">{d.getDate()}</div>
                </div>
                <button onClick={() => onPickDay(iso)} className="p-1 rounded-full hover:bg-white" data-testid={`sch-week-add-${iso}`}><Plus className="w-4 h-4 text-slate-400 hover:text-orange-500" /></button>
              </div>
              <div className="space-y-1">
                {list.map((e, idx) => <EntryCard key={idx} entry={e} compact onClick={() => onEntryClick?.(e)} />)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// DAY VIEW
// ============================================================
function DayView({ anchor, entriesByDate, onAdd, onEntryClick }) {
  const iso = localDateStr(anchor);
  const list = entriesByDate.get(iso) || [];
  return (
    <div className="dashboard-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase font-bold text-orange-600">{iso}</div>
          <h4 className="font-display font-black text-2xl">{anchor.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })}</h4>
        </div>
        <button onClick={() => onAdd(iso)} className="btn-primary text-sm"><Plus className="w-3 h-3" /> Add</button>
      </div>
      {list.length === 0 ? <div className="text-slate-400 text-sm p-6 text-center border-2 border-dashed border-slate-200 rounded-xl">Nothing scheduled. Click &ldquo;Add&rdquo; to create your first entry.</div>
        : <div className="space-y-2">{list.map((e, idx) => <EntryCard key={idx} entry={e} onClick={() => onEntryClick?.(e)} />)}</div>}
    </div>
  );
}

// ============================================================
// YEAR VIEW
// ============================================================
function YearView({ anchor, entriesByDate, onPickDay }) {
  const year = anchor.getFullYear();
  const months = Array.from({ length: 12 }, (_, m) => m);
  return (
    <div className="dashboard-card p-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {months.map((m) => {
          const first = new Date(year, m, 1);
          const startDow = first.getDay();
          const daysInMonth = new Date(year, m + 1, 0).getDate();
          const cells = [];
          for (let i = 0; i < startDow; i++) cells.push(null);
          for (let d = 1; d <= daysInMonth; d++) cells.push(d);
          return (
            <div key={m} className="p-2">
              <div className="font-bold text-slate-800 mb-1">{first.toLocaleDateString(undefined, { month: "long" })}</div>
              <div className="grid grid-cols-7 text-[8px] text-slate-400 mb-1">
                {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => <div key={i} className="text-center">{d}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-0.5">
                {cells.map((d, i) => {
                  if (d == null) return <div key={`e${i}`} />;
                  const iso = `${year}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
                  const hasEntry = (entriesByDate.get(iso) || []).length > 0;
                  return (
                    <button
                      key={iso}
                      onClick={() => onPickDay(iso)}
                      className={`text-[9px] w-6 h-6 rounded-full font-semibold ${hasEntry ? "bg-orange-500 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                    >{d}</button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// ENTRY CARD
// ============================================================
function EntryCard({ entry, compact, onClick }) {
  const cat = CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.personal;
  const time = !entry.all_day && entry.start_at && entry.start_at.length >= 16 ? entry.start_at.slice(11, 16) : (entry.all_day ? "All day" : "");
  return (
    <div
      onClick={onClick}
      className={`rounded-lg p-2 ${cat.bg} ${cat.text} ${compact ? "text-[10px]" : "text-xs"} border border-transparent ${onClick ? "cursor-pointer hover:opacity-80" : ""}`}
      title={entry.title}
      data-testid={`sch-entrycard-${entry.entry_id}`}
    >
      <div className="font-bold truncate">{entry.title}</div>
      {time && <div className={`text-[10px] opacity-80 ${compact ? "hidden" : ""}`}>{time}</div>}
      {entry.location && <div className="text-[10px] opacity-70 truncate flex items-center gap-1"><MapPin className="w-2.5 h-2.5" />{entry.location}</div>}
    </div>
  );
}

// ============================================================
// ADD ENTRY MODAL
// ============================================================
function AddEntryModal({ date, onClose, onSaved }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "", description: "", location: "",
    all_day: true,
    start_date: date, end_date: date,
    start_time: "10:00", end_time: "11:00",
    color: "orange", category: "personal",
    event_id: "", invitee_ids: [],
  });
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [userSearch, setUserSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/events");
        setEvents(r.data || []);
      } catch { /* pms scoped by backend */ }
      try {
        const u = await api.get("/users");
        setUsers((u.data || []).filter((x) => x.user_id !== user.user_id && x.active !== false));
      } catch { /* users scoped by backend */ }
    })();
  }, [user.user_id]);

  const filteredUsers = useMemo(() => {
    const s = userSearch.toLowerCase();
    if (!s) return users;
    return users.filter((u) => `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(s));
  }, [users, userSearch]);

  const toggleInvitee = (uid) => setForm((f) => ({
    ...f, invitee_ids: f.invitee_ids.includes(uid) ? f.invitee_ids.filter((x) => x !== uid) : [...f.invitee_ids, uid],
  }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title, description: form.description, location: form.location,
        all_day: form.all_day,
        start_date: form.start_date, end_date: form.end_date,
        start_at: form.all_day ? `${form.start_date}T00:00:00` : `${form.start_date}T${form.start_time}:00`,
        end_at: form.all_day ? `${form.end_date}T23:59:59` : `${form.end_date}T${form.end_time}:00`,
        color: form.color, category: form.category,
        event_id: form.event_id || null,
        invitee_ids: form.invitee_ids,
      };
      await api.post("/scheduler/entries", payload);
      toast.success(form.event_id ? "Meeting scheduled — principal has been notified" : "Entry created");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[92vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="sch-add-modal">
        <div className="p-5 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold text-orange-500">New entry</div>
            <h3 className="font-display font-black text-xl">Add to your scheduler</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full text-slate-500 hover:bg-slate-100" data-testid="sch-add-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="flex-1 overflow-y-auto p-5 space-y-3">
          <input className="input-pill" placeholder="Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sch-title" />
          <input className="input-pill" placeholder="Location or Video Call" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="sch-location" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.all_day} onChange={(e) => setForm({ ...form, all_day: e.target.checked })} />
            All day
          </label>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold text-slate-500 block mb-1">Starts</label>
              <input type="date" className="input-pill" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="sch-start-date" />
              {!form.all_day && <input type="time" className="input-pill mt-1" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="sch-start-time" />}
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-500 block mb-1">Ends</label>
              <input type="date" className="input-pill" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="sch-end-date" />
              {!form.all_day && <input type="time" className="input-pill mt-1" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="sch-end-time" />}
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-500 block mb-1">Category</label>
            <div className="flex flex-wrap gap-1">
              {["personal", "work", "meeting", "travel", "reminder"].map((c) => {
                const on = form.category === c;
                const col = CATEGORY_COLORS[c];
                return <button key={c} type="button" onClick={() => setForm({ ...form, category: c })} className={`px-2.5 py-1 text-xs font-bold rounded-full capitalize ${on ? `${col.bg} ${col.text}` : "bg-slate-100 text-slate-500"}`}>{c}</button>;
              })}
            </div>
          </div>
          <textarea className="input-pill resize-none" rows={3} placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="sch-desc" />

          <div className="pt-3 border-t border-slate-100">
            <label className="text-[10px] font-bold text-slate-500 block mb-1">Meeting with an event&rsquo;s school? (auto-emails the principal)</label>
            <select className="input-pill" value={form.event_id} onChange={(e) => setForm({ ...form, event_id: e.target.value })} data-testid="sch-event-select">
              <option value="">— Not a school meeting —</option>
              {events.map((e) => <option key={e.event_id} value={e.event_id}>{e.name} — {e.city || "city?"}</option>)}
            </select>
          </div>

          <div className="pt-3 border-t border-slate-100">
            <label className="text-[10px] font-bold text-slate-500 block mb-1">Invite colleagues (they&rsquo;ll see this on their scheduler)</label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input className="input-pill text-sm pl-9" placeholder="Search…" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} data-testid="sch-invitee-search" />
            </div>
            <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-xl mt-2 p-1 bg-slate-50/40">
              {filteredUsers.slice(0, 60).map((u) => {
                const on = form.invitee_ids.includes(u.user_id);
                return (
                  <label key={u.user_id} className={`flex items-center gap-2 px-2 py-1.5 text-sm rounded cursor-pointer ${on ? "bg-orange-100" : "hover:bg-white"}`} data-testid={`sch-invitee-${u.user_id}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleInvitee(u.user_id)} />
                    <span className="flex-1 truncate">{u.name}</span>
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">{ROLE_LABELS[u.role] || u.role}</span>
                  </label>
                );
              })}
              {filteredUsers.length === 0 && <div className="text-xs text-slate-400 p-2 text-center">No matches</div>}
            </div>
          </div>
        </form>
        <div className="p-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost">Cancel</button>
          <button type="button" onClick={save} disabled={saving || !form.title.trim()} className="btn-primary px-5 disabled:opacity-50" data-testid="sch-save">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <>Save</>}
          </button>
        </div>
      </div>
    </div>
  );
}
